// src/modules/ida/views/Documents.tsx
//
// What? Document management view for IDA module.
// Why? To allow users to upload, view, and manage documents for IDA indexing.
// How? Provides upload, list, and delete functionality with file tree view.

import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Stack,
  IconButton,
  Chip,
  CircularProgress,
  Modal,
  ModalDialog,
  ModalClose,
  Alert,
  Tooltip,
  LinearProgress,
  Checkbox,
  Input,
  FormControl,
  FormLabel,
} from "@mui/joy";
import { useWorkspaceContext } from "../../../providers/WorkspaceProvider";
import Page from "../../../components/Pages/Page";
import PageHeader from "../../../components/PageHeader";
import DescriptionIcon from "@mui/icons-material/Description";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CreateNewFolderIcon from "@mui/icons-material/CreateNewFolder";
import DeleteIcon from "@mui/icons-material/Delete";
import ArticleIcon from "@mui/icons-material/Article";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import TableChartIcon from "@mui/icons-material/TableChart";
import SlideshowIcon from "@mui/icons-material/Slideshow";
import ImageIcon from "@mui/icons-material/Image";
import AudioFileIcon from "@mui/icons-material/AudioFile";
import VideoFileIcon from "@mui/icons-material/VideoFile";
import CodeIcon from "@mui/icons-material/Code";
import DataObjectIcon from "@mui/icons-material/DataObject";
import TextSnippetIcon from "@mui/icons-material/TextSnippet";
import RefreshIcon from "@mui/icons-material/Refresh";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import FolderIcon from "@mui/icons-material/Folder";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import DriveFolderUploadIcon from "@mui/icons-material/DriveFolderUpload";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import AddIcon from "@mui/icons-material/Add";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import ErrorIcon from "@mui/icons-material/Error";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  useListDocuments,
  useUploadDocument,
  useDeleteDocument,
  useDeleteDocuments,
  useDownloadDocument,
  useGetVersionHistory,
  useRestoreVersion,
  useGetDocumentForEdit,
  useSaveDocumentContent,
  useUploadDocumentVersion,
  useStartExportZip,
  useGetExportStatus,
  useListExportJobs,
  useDownloadExport,
  useDismissExportJob,
  useMoveDocuments,
  useListFolders,
  useCreateFolder,
  useDeleteFolder,
  useSharePointSync,
} from "../services/api";
import type {
  IDADocument,
  IDAVersionHistory,
  ExportJob,
} from "../common/types";

import HistoryIcon from "@mui/icons-material/History";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import RestoreIcon from "@mui/icons-material/Restore";
import DownloadIcon from "@mui/icons-material/Download";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import FolderZipIcon from "@mui/icons-material/FolderZip";
import LockIcon from "@mui/icons-material/Lock";
import PublicIcon from "@mui/icons-material/Public";
import BusinessIcon from "@mui/icons-material/Business";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import Select from "@mui/joy/Select";
import Option from "@mui/joy/Option";
import CloudSyncIcon from "@mui/icons-material/CloudSync";
import DocumentEditorModal from "../components/DocumentEditorModal";
import DocumentPreviewModal from "../components/DocumentPreviewModal";
import VersionDiffView from "../components/VersionDiffView";
import { useQuotaStatus } from "../../../services/api";

export const DOCUMENT_UPLOAD_ALLOWED_EXTENSIONS = [
  ".pdf",
  ".docx",
  ".doc",
  ".txt",
  ".md",
  ".csv",
  ".json",
  ".html",
  ".xlsx",
  ".xls",
  ".pptx",
  ".ppt",
  ".png",
  ".jpg",
  ".jpeg",
  ".webp",
];

// Tree node type
interface TreeNode {
  type: "folder" | "file";
  name: string;
  path: string;
  children?: TreeNode[];
  document?: IDADocument;
}

export type SortColumn =
  "name" | "size" | "tokens" | "pages" | "uploaded" | "status" | "version";

export type SortDirection = "asc" | "desc";

export interface DocumentSortState {
  column: SortColumn;
  direction: SortDirection;
}

const DOCUMENT_TABLE_MIN_WIDTH = 900;

export const DEFAULT_DOCUMENT_SORT: DocumentSortState = {
  column: "name",
  direction: "asc",
};

const DOCUMENT_SORT_COLUMNS: SortColumn[] = [
  "name",
  "size",
  "tokens",
  "pages",
  "uploaded",
  "status",
  "version",
];

const DOCUMENT_SORT_STORAGE_PREFIX = "ida.documents.sort";

const getDocumentSortStorageKey = (workspaceId: string) =>
  `${DOCUMENT_SORT_STORAGE_PREFIX}.${workspaceId}`;

const isSortColumn = (value: unknown): value is SortColumn =>
  typeof value === "string" &&
  DOCUMENT_SORT_COLUMNS.includes(value as SortColumn);

const isSortDirection = (value: unknown): value is SortDirection =>
  value === "asc" || value === "desc";

export const readDocumentSortPreference = (
  workspaceId?: string | null,
): DocumentSortState => {
  if (!workspaceId || typeof window === "undefined") {
    return DEFAULT_DOCUMENT_SORT;
  }

  try {
    const stored = window.localStorage.getItem(
      getDocumentSortStorageKey(workspaceId),
    );
    if (!stored) return DEFAULT_DOCUMENT_SORT;

    const parsed = JSON.parse(stored);
    if (isSortColumn(parsed?.column) && isSortDirection(parsed?.direction)) {
      return { column: parsed.column, direction: parsed.direction };
    }
  } catch {
    // Ignore malformed or inaccessible localStorage and fall back to default.
  }

  return DEFAULT_DOCUMENT_SORT;
};

const writeDocumentSortPreference = (
  workspaceId: string | undefined,
  sortState: DocumentSortState,
) => {
  if (!workspaceId || typeof window === "undefined") return;

  try {
    window.localStorage.setItem(
      getDocumentSortStorageKey(workspaceId),
      JSON.stringify(sortState),
    );
  } catch {
    // Non-critical preference persistence failure.
  }
};

export const getNextDocumentSort = (
  current: DocumentSortState,
  column: SortColumn,
): DocumentSortState => {
  if (current.column === column) {
    return {
      column,
      direction: current.direction === "asc" ? "desc" : "asc",
    };
  }

  return {
    column,
    direction: column === "uploaded" ? "desc" : "asc",
  };
};

const directionMultiplier = (direction: SortDirection) =>
  direction === "asc" ? 1 : -1;

const compareStrings = (
  valueA: string | undefined | null,
  valueB: string | undefined | null,
  direction: SortDirection,
) => {
  return (
    (valueA ?? "").localeCompare(valueB ?? "", undefined, {
      sensitivity: "base",
      numeric: true,
    }) * directionMultiplier(direction)
  );
};

const asFiniteNumber = (value: unknown): number | null => {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
};

const asTimestamp = (value: unknown): number | null => {
  if (typeof value !== "string" || !value) return null;
  const timestamp = new Date(value).valueOf();
  return Number.isFinite(timestamp) ? timestamp : null;
};

const compareNullableNumbers = (
  valueA: number | null,
  valueB: number | null,
  direction: SortDirection,
) => {
  const aMissing = valueA === null;
  const bMissing = valueB === null;

  if (aMissing && bMissing) return 0;
  if (aMissing) return 1;
  if (bMissing) return -1;
  if (valueA === valueB) return 0;

  return (valueA < valueB ? -1 : 1) * directionMultiplier(direction);
};

export const compareDocumentsForSort = (
  docA: IDADocument,
  docB: IDADocument,
  sortState: DocumentSortState,
): number => {
  let primary = 0;

  switch (sortState.column) {
    case "name":
      primary = compareStrings(
        docA.filename,
        docB.filename,
        sortState.direction,
      );
      break;
    case "size":
      primary = compareNullableNumbers(
        asFiniteNumber(docA.file_size),
        asFiniteNumber(docB.file_size),
        sortState.direction,
      );
      break;
    case "tokens":
      primary = compareNullableNumbers(
        asFiniteNumber(docA.token_count),
        asFiniteNumber(docB.token_count),
        sortState.direction,
      );
      break;
    case "pages":
      primary = compareNullableNumbers(
        asFiniteNumber(docA.page_count),
        asFiniteNumber(docB.page_count),
        sortState.direction,
      );
      break;
    case "uploaded":
      primary = compareNullableNumbers(
        asTimestamp(docA.uploaded_at),
        asTimestamp(docB.uploaded_at),
        sortState.direction,
      );
      break;
    case "status":
      primary = compareStrings(
        docA.index_status,
        docB.index_status,
        sortState.direction,
      );
      break;
    case "version":
      primary = compareNullableNumbers(
        asFiniteNumber(docA.version),
        asFiniteNumber(docB.version),
        sortState.direction,
      );
      break;
  }

  if (primary !== 0) return primary;

  const nameFallback = compareStrings(docA.filename, docB.filename, "asc");
  if (nameFallback !== 0) return nameFallback;

  return docA.id.localeCompare(docB.id);
};

interface DocumentSortHeaderProps {
  label: string;
  column: SortColumn;
  sortState: DocumentSortState;
  onSort: (column: SortColumn) => void;
  width?: number;
  flex?: number;
  align?: "left" | "right" | "center";
}

export const DocumentSortHeader: React.FC<DocumentSortHeaderProps> = ({
  label,
  column,
  sortState,
  onSort,
  width,
  flex,
  align = "left",
}) => {
  const active = sortState.column === column;
  const justifyContent =
    align === "right"
      ? "flex-end"
      : align === "center"
        ? "center"
        : "flex-start";

  return (
    <Box
      component="button"
      type="button"
      aria-label={`Sort by ${label}`}
      aria-pressed={active}
      data-testid={`documents-sort-header-${column}`}
      onClick={() => onSort(column)}
      sx={{
        width,
        flex: flex ?? (width !== undefined ? `0 0 ${width}px` : undefined),
        minWidth: 0,
        flexShrink: width !== undefined ? 0 : undefined,
        p: 0,
        border: 0,
        bgcolor: "transparent",
        color: active ? "primary.plainColor" : "text.tertiary",
        display: "flex",
        alignItems: "center",
        justifyContent,
        gap: 0.25,
        cursor: "pointer",
        font: "inherit",
        "&:hover": {
          color: "primary.plainColor",
        },
      }}
    >
      <Typography
        component="span"
        level="body-xs"
        fontWeight="lg"
        sx={{ color: "inherit" }}
      >
        {label}
      </Typography>
      {active &&
        (sortState.direction === "asc" ? (
          <KeyboardArrowUpIcon
            data-testid={`documents-sort-${column}-chevron-asc`}
            sx={{ fontSize: 16 }}
          />
        ) : (
          <KeyboardArrowDownIcon
            data-testid={`documents-sort-${column}-chevron-desc`}
            sx={{ fontSize: 16 }}
          />
        ))}
    </Box>
  );
};

const Documents: React.FC = () => {
  const { workspace } = useWorkspaceContext();
  const { persistentExceeded } = useQuotaStatus();
  const [documents, setDocuments] = useState<IDADocument[]>([]);
  const [documentSort, setDocumentSort] = useState<DocumentSortState>(
    DEFAULT_DOCUMENT_SORT,
  );
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<IDADocument | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());
  const [batchDeleteConfirm, setBatchDeleteConfirm] = useState(false);
  const [folderDeleteConfirm, setFolderDeleteConfirm] =
    useState<TreeNode | null>(null);
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(
    new Set(["/"]),
  );

  // Upload target folder (which folder to upload files to)
  const [uploadTargetFolder, setUploadTargetFolder] = useState<string>("/");

  // Create folder modal state
  const [createFolderModal, setCreateFolderModal] = useState(false);
  const [createFolderParent, setCreateFolderParent] = useState<string>("/");
  const [newFolderName, setNewFolderName] = useState("");

  // Hover state for folder actions
  const [hoveredFolder, setHoveredFolder] = useState<string | null>(null);

  const listDocuments = useListDocuments();
  const uploadDocument = useUploadDocument();
  const deleteDocument = useDeleteDocument();
  const deleteDocuments = useDeleteDocuments();
  const downloadDocument = useDownloadDocument();
  const getVersionHistory = useGetVersionHistory();
  const restoreVersion = useRestoreVersion();
  const triggerSharePointSync = useSharePointSync();
  const [spSyncing, setSpSyncing] = useState(false);
  const [spSyncErrors, setSpSyncErrors] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);
  const versionFileInputRef = useRef<HTMLInputElement>(null);
  const [versionUploadTarget, setVersionUploadTarget] =
    useState<IDADocument | null>(null);
  const [versionUploadingId, setVersionUploadingId] = useState<string | null>(
    null,
  );
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  // Version modal state
  const [versionModalDoc, setVersionModalDoc] = useState<IDADocument | null>(
    null,
  );
  const [versionHistory, setVersionHistory] =
    useState<IDAVersionHistory | null>(null);
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(
    null,
  );
  const [versionLoading, setVersionLoading] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [showDiff, setShowDiff] = useState(false);

  // Document editor modal state
  const [editorDocument, setEditorDocument] = useState<IDADocument | null>(
    null,
  );
  const [editorContent, setEditorContent] = useState("");
  const [editorOriginalFormat, setEditorOriginalFormat] = useState<
    string | undefined
  >();
  const [editorOpen, setEditorOpen] = useState(false);
  const [, setEditorLoading] = useState(false);
  const [editorSaving, setEditorSaving] = useState(false);
  const getDocumentForEdit = useGetDocumentForEdit();
  const saveDocumentContent = useSaveDocumentContent();
  const uploadDocumentVersion = useUploadDocumentVersion();

  // Document preview modal state
  const [previewDocument, setPreviewDocument] = useState<IDADocument | null>(
    null,
  );
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewSpVersionId, setPreviewSpVersionId] = useState<
    string | undefined
  >(undefined);

  // Export ZIP hooks
  const startExportZip = useStartExportZip();
  const getExportStatus = useGetExportStatus();
  const listExportJobs = useListExportJobs();
  const downloadExport = useDownloadExport();
  const dismissExportJob = useDismissExportJob();

  // Export job state
  const [exportJobs, setExportJobs] = useState<ExportJob[]>([]);
  const [downloadingExportId, setDownloadingExportId] = useState<string | null>(
    null,
  );

  // Drag and drop state
  const moveDocuments = useMoveDocuments();
  const [dragOverFolder, setDragOverFolder] = useState<string | null>(null);

  // Folder management hooks
  const listFoldersApi = useListFolders();
  const createFolderApi = useCreateFolder();
  const deleteFolderApi = useDeleteFolder();
  const [persistedFolders, setPersistedFolders] = useState<string[]>([]);

  useEffect(() => {
    setDocumentSort(readDocumentSortPreference(workspace?.id));
  }, [workspace?.id]);

  const handleDocumentSort = useCallback(
    (column: SortColumn) => {
      setDocumentSort((current) => {
        const next = getNextDocumentSort(current, column);
        writeDocumentSortPreference(workspace?.id, next);
        return next;
      });
    },
    [workspace?.id],
  );

  // Strip SharePoint drive path prefix so only the user-visible folder name is shown.
  // Graph API stores paths like /drives/{driveId}/root:/My Folder — we want /My Folder.
  // This also handles legacy docs stored before the backend strip was fixed.
  const cleanFolderPath = (path: string): string => {
    if (!path || path === "/") return "/";
    const idx = path.indexOf("/root:");
    if (idx !== -1) {
      const after = path.slice(idx + "/root:".length);
      return after || "/";
    }
    return path;
  };

  // Check if there are any folders (non-root)
  const hasFolders = useMemo(() => {
    return (
      persistedFolders.length > 0 ||
      documents.some((doc) => doc.folder && doc.folder !== "/")
    );
  }, [documents, persistedFolders]);

  // Count distinct non-root folders for drag-and-drop on folders
  const folderCount = useMemo(() => {
    const folders = new Set<string>();
    for (const doc of documents) {
      const cleaned = cleanFolderPath(doc.folder || "/");
      if (cleaned !== "/") {
        const topFolder = "/" + cleaned.split("/").filter(Boolean)[0];
        folders.add(topFolder);
      }
    }
    for (const fp of persistedFolders) {
      const cleaned = cleanFolderPath(fp);
      if (cleaned !== "/") {
        const topFolder = "/" + cleaned.split("/").filter(Boolean)[0];
        folders.add(topFolder);
      }
    }
    return folders.size;
  }, [documents, persistedFolders]);

  // Build folder tree from flat document list
  const folderTree = useMemo(() => {
    const root: TreeNode = {
      type: "folder",
      name: "/",
      path: "/",
      children: [],
    };

    // Group by folder path, then apply the selected document sort within each folder.
    const sortedDocs = [...documents].sort((a, b) => {
      const folderA = cleanFolderPath(a.folder || "/");
      const folderB = cleanFolderPath(b.folder || "/");
      if (folderA !== folderB) return folderA.localeCompare(folderB);
      return compareDocumentsForSort(a, b, documentSort);
    });

    for (const doc of sortedDocs) {
      const folderPath = cleanFolderPath(doc.folder || "/");
      const parts =
        folderPath === "/" ? [] : folderPath.split("/").filter(Boolean);

      let current = root;
      let currentPath = "";

      // Create folder nodes
      for (const part of parts) {
        currentPath = currentPath + "/" + part;
        let folderNode = current.children?.find(
          (c) => c.type === "folder" && c.name === part,
        );
        if (!folderNode) {
          folderNode = {
            type: "folder",
            name: part,
            path: currentPath,
            children: [],
          };
          current.children!.push(folderNode);
        }
        current = folderNode;
      }

      // Add document to current folder
      current.children!.push({
        type: "file",
        name: doc.filename,
        path: folderPath + "/" + doc.id,
        document: doc,
      });
    }

    // Add persisted empty folders that don't already exist in the tree
    for (const rawFolderPath of persistedFolders) {
      const folderPath = cleanFolderPath(rawFolderPath);
      const parts = folderPath.split("/").filter(Boolean);
      let current = root;
      let currentPath = "";

      for (const part of parts) {
        currentPath = currentPath + "/" + part;
        let folderNode = current.children?.find(
          (c) => c.type === "folder" && c.name === part,
        );
        if (!folderNode) {
          folderNode = {
            type: "folder",
            name: part,
            path: currentPath,
            children: [],
          };
          current.children!.push(folderNode);
        }
        current = folderNode;
      }
    }

    return root;
  }, [documents, documentSort, persistedFolders]);

  // Track if a request is in progress to prevent overlapping polls
  const isLoadingRef = useRef(false);

  // Load documents (silent mode for polling to avoid spinner flicker)
  const loadDocuments = useCallback(
    async (silent = false) => {
      if (!workspace?.id) return;

      // Prevent overlapping requests during polling
      if (silent && isLoadingRef.current) {
        return;
      }

      isLoadingRef.current = true;
      if (!silent) {
        setLoading(true);
        setError(null);
      }
      try {
        const [docs, folders] = await Promise.all([
          listDocuments(workspace.id),
          listFoldersApi(workspace.id),
        ]);
        setDocuments(docs);
        setPersistedFolders(folders);
        // Clear selections for documents that no longer exist
        setSelectedIds((prev) => {
          const docIds = new Set(docs.map((d) => d.id));
          const newSelected = new Set([...prev].filter((id) => docIds.has(id)));
          return newSelected.size !== prev.size ? newSelected : prev;
        });
      } catch (err) {
        console.error("Failed to load documents:", err);
        if (!silent) {
          setError("Failed to load documents");
        }
      } finally {
        isLoadingRef.current = false;
        if (!silent) {
          setLoading(false);
        }
      }
    },
    [workspace?.id, listDocuments, listFoldersApi],
  );

  // Load documents on mount and trigger SP sync silently
  useEffect(() => {
    if (!workspace?.id) return;
    loadDocuments();
    if (workspace.enabled_integrations?.includes("sharepoint") !== true) return;
    // Trigger SharePoint sync in background for the workspace's enabled integration selection.
    setSpSyncing(true);
    setSpSyncErrors([]);
    triggerSharePointSync(workspace.id)
      .then((syncResult) => {
        if (syncResult.errors?.length) {
          setSpSyncErrors(syncResult.errors);
        }
        return loadDocuments(true);
      })
      .catch(() => {})
      .finally(() => setSpSyncing(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspace?.id]);

  // Poll for updates when there are pending/processing/syncing documents
  useEffect(() => {
    const hasPendingDocs = documents.some(
      (doc) =>
        doc.index_status === "pending" ||
        doc.index_status === "processing" ||
        doc.index_status === "syncing",
    );

    if (!hasPendingDocs) return;

    const pollInterval = setInterval(() => {
      loadDocuments(true); // Silent poll - no loading spinner
    }, 5000); // Poll every 5 seconds

    return () => clearInterval(pollInterval);
  }, [documents, loadDocuments]);

  // Auto-dismiss success messages after 5 seconds
  useEffect(() => {
    if (!success) return;
    const timer = setTimeout(() => setSuccess(null), 5000);
    return () => clearTimeout(timer);
  }, [success]);

  // Load export jobs on mount
  useEffect(() => {
    const loadExportJobs = async () => {
      if (!workspace?.id) return;
      try {
        const result = await listExportJobs(workspace.id);
        setExportJobs(result.jobs || []);
      } catch (err) {
        console.error("Failed to load export jobs:", err);
      }
    };
    loadExportJobs();
  }, [workspace?.id, listExportJobs]);

  // Track if we have active export jobs (for polling)
  const hasActiveExportJobs = exportJobs.some(
    (j) => j.status === "pending" || j.status === "processing",
  );

  // Poll for active export job updates
  useEffect(() => {
    if (!hasActiveExportJobs || !workspace?.id) return;

    let isCancelled = false;

    const poll = async () => {
      if (isCancelled) return;

      // Get current active jobs from state
      setExportJobs((currentJobs) => {
        const activeJobs = currentJobs.filter(
          (j) => j.status === "pending" || j.status === "processing",
        );

        // Poll each active job
        for (const job of activeJobs) {
          getExportStatus(workspace.id, job.job_id)
            // eslint-disable-next-line no-loop-func
            .then((status) => {
              if (!isCancelled) {
                setExportJobs((prev) =>
                  prev.map((j) => (j.job_id === job.job_id ? status : j)),
                );
              }
            })
            .catch(() => {
              // Silently ignore - job may have been dismissed or expired
            });
        }

        return currentJobs; // Don't modify state in this callback
      });
    };

    const pollInterval = setInterval(poll, 3000);

    return () => {
      isCancelled = true;
      clearInterval(pollInterval);
    };
  }, [hasActiveExportJobs, workspace?.id, getExportStatus]);

  // Allowed file extensions for upload
  // Max file size: 10MB (MongoDB BSON limit is ~16MB, base64 adds ~33% overhead)
  const MAX_FILE_SIZE = 10 * 1024 * 1024;

  // Check if a file has an allowed extension
  const isAllowedFile = (filename: string): boolean => {
    const ext = filename.toLowerCase().slice(filename.lastIndexOf("."));
    return DOCUMENT_UPLOAD_ALLOWED_EXTENSIONS.includes(ext);
  };

  // Check if a file is within size limit
  const isAllowedFileSize = (file: File): boolean => {
    return file.size > 0 && file.size <= MAX_FILE_SIZE;
  };

  // Format file size for display
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Trigger upload for files
  const triggerFileUpload = (targetFolder: string) => {
    setUploadTargetFolder(targetFolder);
    setTimeout(() => {
      fileInputRef.current?.click();
    }, 0);
  };

  // Trigger upload for folders
  const triggerFolderUpload = (targetFolder: string) => {
    setUploadTargetFolder(targetFolder);
    setTimeout(() => {
      folderInputRef.current?.click();
    }, 0);
  };

  // Handle file selection (supports both files and folders via webkitdirectory)
  const handleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const files = event.target.files;
    if (!files || files.length === 0 || !workspace?.id || persistentExceeded)
      return;

    setUploading(true);
    setError(null);

    const fileArray = Array.from(files);

    // Debug: Log all files received from browser
    console.log(`[Upload] Browser returned ${fileArray.length} files:`);
    const folderCounts: Record<string, number> = {};
    for (const file of fileArray) {
      const relativePath =
        (file as File & { webkitRelativePath?: string }).webkitRelativePath ||
        file.name;
      const parts = relativePath.split("/");
      const topFolder = parts[0] || "(root)";
      folderCounts[topFolder] = (folderCounts[topFolder] || 0) + 1;
    }
    console.log("[Upload] Files per top-level folder:", folderCounts);

    // Check if this is a folder upload (files have webkitRelativePath)
    const isFolderUpload = fileArray.some((f) =>
      (
        f as File & { webkitRelativePath?: string }
      ).webkitRelativePath?.includes("/"),
    );

    // Separate files by type and size
    const skippedFormat: File[] = [];
    const skippedSize: File[] = [];
    const allowedFiles: File[] = [];

    for (const file of fileArray) {
      if (!isAllowedFile(file.name)) {
        skippedFormat.push(file);
      } else if (!isAllowedFileSize(file)) {
        skippedSize.push(file);
      } else {
        allowedFiles.push(file);
      }
    }

    if (allowedFiles.length === 0) {
      const reasons: string[] = [];
      if (skippedFormat.length > 0) {
        reasons.push(
          `Supported formats: ${DOCUMENT_UPLOAD_ALLOWED_EXTENSIONS.join(", ")}`,
        );
      }
      if (skippedSize.length > 0) {
        reasons.push(`Files must be non-empty and no larger than 10MB`);
      }
      setError(`No uploadable files found. ${reasons.join(". ")}`);
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      if (folderInputRef.current) {
        folderInputRef.current.value = "";
      }
      return;
    }

    const failedUploads: string[] = [];

    try {
      // Prepare all uploads with their folder paths
      const uploads = allowedFiles.map((file) => {
        let folder = uploadTargetFolder;
        if (isFolderUpload) {
          const relativePath =
            (file as File & { webkitRelativePath?: string })
              .webkitRelativePath || "";
          const lastSlash = relativePath.lastIndexOf("/");
          if (lastSlash > 0) {
            const subPath = relativePath.substring(0, lastSlash);
            folder =
              uploadTargetFolder === "/"
                ? "/" + subPath
                : uploadTargetFolder + "/" + subPath;
          }
        }
        return { file, folder };
      });

      // Upload in parallel batches of 10
      const BATCH_SIZE = 10;
      let completed = 0;

      for (let i = 0; i < uploads.length; i += BATCH_SIZE) {
        const batch = uploads.slice(i, i + BATCH_SIZE);
        setUploadProgress(
          `Uploading ${completed + 1}-${Math.min(completed + batch.length, uploads.length)} of ${uploads.length}...`,
        );

        const results = await Promise.allSettled(
          batch.map(({ file, folder }) =>
            uploadDocument(workspace.id, file, folder),
          ),
        );

        // Track failures
        results.forEach((result, idx) => {
          if (result.status === "rejected") {
            console.error(
              `Failed to upload ${batch[idx].file.name}:`,
              result.reason,
            );
            failedUploads.push(batch[idx].file.name);
          }
        });

        completed += batch.length;
      }

      setUploadProgress(null);
      loadDocuments();

      // Build summary message
      const uploadedCount = allowedFiles.length - failedUploads.length;
      const summaryParts: string[] = [];

      // Debug: Log upload summary
      console.log(
        `[Upload] Summary: ${uploadedCount} uploaded, ${failedUploads.length} failed, ${skippedFormat.length} wrong format, ${skippedSize.length} empty/too large`,
      );

      if (uploadedCount > 0) {
        setSuccess(
          `Uploaded ${uploadedCount} file${uploadedCount !== 1 ? "s" : ""}`,
        );
      }

      if (skippedFormat.length > 0) {
        const skippedNames = skippedFormat.slice(0, 3).map((f) => f.name);
        const moreCount = skippedFormat.length - 3;
        summaryParts.push(
          `${skippedFormat.length} skipped (unsupported format): ${skippedNames.join(", ")}${moreCount > 0 ? ` +${moreCount} more` : ""}`,
        );
      }
      if (skippedSize.length > 0) {
        const skippedNames = skippedSize
          .slice(0, 3)
          .map((f) => `${f.name} (${formatFileSize(f.size)})`);
        const moreCount = skippedSize.length - 3;
        summaryParts.push(
          `${skippedSize.length} skipped (empty or >10MB): ${skippedNames.join(", ")}${moreCount > 0 ? ` +${moreCount} more` : ""}`,
        );
      }
      if (failedUploads.length > 0) {
        summaryParts.push(
          `${failedUploads.length} failed: ${failedUploads.join(", ")}`,
        );
      }

      if (summaryParts.length > 0) {
        setError(summaryParts.join(". "));
      }
    } catch (err) {
      console.error("Failed to upload:", err);
      setError(
        `Failed to upload: ${(err as any)?.response?.detail || (err as Error).message}`,
      );
    } finally {
      setUploading(false);
      setUploadProgress(null);
      setUploadTargetFolder("/");
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      if (folderInputRef.current) {
        folderInputRef.current.value = "";
      }
    }
  };

  // Handle create folder
  const handleCreateFolder = async () => {
    if (!newFolderName.trim() || !workspace?.id) return;

    // Normalize folder name (remove slashes, trim)
    const folderName = newFolderName.trim().replace(/\//g, "-");
    const newPath =
      createFolderParent === "/"
        ? "/" + folderName
        : createFolderParent + "/" + folderName;

    setCreateFolderModal(false);
    setNewFolderName("");

    try {
      await createFolderApi(workspace.id, newPath);
      // Expand the parent folder and the new folder
      setExpandedFolders(
        (prev) => new Set([...prev, createFolderParent, newPath]),
      );
      loadDocuments();
    } catch {
      setError("Failed to create folder");
    }
  };

  // Open create folder modal
  const openCreateFolderModal = (parentPath: string) => {
    setCreateFolderParent(parentPath);
    setNewFolderName("");
    setCreateFolderModal(true);
  };

  // Handle single document delete
  const handleDelete = async () => {
    if (!deleteConfirm || !workspace?.id) return;

    const docId = deleteConfirm.id;
    const filename = deleteConfirm.filename;
    setDeleteConfirm(null);
    setDeletingIds((prev) => new Set([...prev, docId]));
    setError(null);
    setSuccess(null);

    try {
      await deleteDocument(workspace.id, docId);
      setDocuments((prev) => prev.filter((d) => d.id !== docId));
      setSelectedIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(docId);
        return newSet;
      });
      setSuccess(`Deleted "${filename}"`);
    } catch (err) {
      console.error("Failed to delete document:", err);
      setError(
        `Failed to delete: ${(err as any)?.response?.detail || (err as Error).message}`,
      );
    } finally {
      setDeletingIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(docId);
        return newSet;
      });
    }
  };

  // Handle batch delete
  const handleBatchDelete = async () => {
    if (selectedIds.size === 0 || !workspace?.id) return;

    const idsToDelete = [...selectedIds];
    const deleteCount = idsToDelete.length;
    setBatchDeleteConfirm(false);
    setDeletingIds(new Set(idsToDelete));
    setError(null);
    setSuccess(null);

    try {
      const result = await deleteDocuments(workspace.id, idsToDelete);
      const deletedSet = new Set(result.deleted);
      setDocuments((prev) => prev.filter((d) => !deletedSet.has(d.id)));
      setSelectedIds(new Set());

      if (result.failed.length > 0) {
        setError(
          `Failed to delete ${result.failed.length} of ${deleteCount} document(s)`,
        );
      } else {
        setSuccess(`Successfully deleted ${result.deleted.length} document(s)`);
      }
    } catch (err) {
      console.error("Failed to delete documents:", err);
      setError(
        `Failed to delete: ${(err as any)?.response?.detail || (err as Error).message}`,
      );
    } finally {
      setDeletingIds(new Set());
    }
  };

  // Handle folder delete
  const handleFolderDelete = async () => {
    if (!folderDeleteConfirm || !workspace?.id) return;

    const idsToDelete = getDocIdsInFolder(folderDeleteConfirm);
    const folderName = folderDeleteConfirm.name;
    const folderPath = folderDeleteConfirm.path;
    setFolderDeleteConfirm(null);
    setError(null);
    setSuccess(null);

    // Always delete the persisted folder record
    try {
      await deleteFolderApi(workspace.id, folderPath);
    } catch {
      // Folder record may not exist if it was implicit
    }

    if (idsToDelete.length === 0) {
      // Empty folder -- just refresh to remove it from the tree
      setSuccess(`Deleted folder "${folderName}"`);
      loadDocuments();
      return;
    }

    setDeletingIds(new Set(idsToDelete));

    try {
      const result = await deleteDocuments(workspace.id, idsToDelete);
      const deletedSet = new Set(result.deleted);
      setDocuments((prev) => prev.filter((d) => !deletedSet.has(d.id)));
      setSelectedIds((prev) => {
        const newSet = new Set(prev);
        for (const id of result.deleted) {
          newSet.delete(id);
        }
        return newSet;
      });

      if (result.failed.length > 0) {
        setError(
          `Failed to delete ${result.failed.length} file(s) in "${folderName}"`,
        );
      } else {
        setSuccess(
          `Deleted folder "${folderName}" (${result.deleted.length} files)`,
        );
      }
      loadDocuments();
    } catch (err) {
      console.error("Failed to delete folder:", err);
      setError(
        `Failed to delete: ${(err as any)?.response?.detail || (err as Error).message}`,
      );
    } finally {
      setDeletingIds(new Set());
    }
  };

  // Handle document download
  const handleDownload = async (doc: IDADocument) => {
    if (!workspace?.id || downloadingId) return;

    setDownloadingId(doc.id);
    try {
      await downloadDocument(workspace.id, doc.id, doc.filename);
    } catch (err) {
      console.error("Failed to download document:", err);
      setError(
        `Failed to download: ${(err as any)?.response?.detail || (err as Error).message}`,
      );
    } finally {
      setDownloadingId(null);
    }
  };

  // Open version history modal
  const handleOpenVersionModal = async (doc: IDADocument) => {
    if (!workspace?.id) return;

    setVersionModalDoc(doc);
    setVersionLoading(true);
    setVersionHistory(null);
    setShowDiff(false);
    setSelectedVersionId(doc.id); // Default to current version

    try {
      const history = await getVersionHistory(workspace.id, doc.id);
      setVersionHistory(history);
      // Select the current version by default
      const currentVersion = history.versions.find((v) => v.is_current);
      if (currentVersion) {
        setSelectedVersionId(currentVersion.id);
      }
    } catch (err) {
      console.error("Failed to fetch version history:", err);
      setError(
        `Failed to fetch version history: ${(err as any)?.response?.detail || (err as Error).message}`,
      );
      setVersionModalDoc(null);
    } finally {
      setVersionLoading(false);
    }
  };

  // Close version modal
  const handleCloseVersionModal = () => {
    setVersionModalDoc(null);
    setVersionHistory(null);
    setSelectedVersionId(null);
    setShowDiff(false);
  };

  // Download selected version
  const handleDownloadVersion = async () => {
    if (!workspace?.id || !selectedVersionId || !versionHistory) return;

    const selectedVersion = versionHistory.versions.find(
      (v) => v.id === selectedVersionId,
    );
    if (!selectedVersion) return;

    setDownloadingId(selectedVersionId);
    try {
      const spVersionId = selectedVersion.sp_version_id;
      await downloadDocument(
        workspace.id,
        spVersionId
          ? (versionModalDoc?.id ?? selectedVersionId)
          : selectedVersionId,
        selectedVersion.filename,
        { spVersionId },
      );
    } catch (err) {
      console.error("Failed to download version:", err);
      setError(
        `Failed to download: ${(err as any)?.response?.detail || (err as Error).message}`,
      );
    } finally {
      setDownloadingId(null);
    }
  };

  // Restore selected version
  const handleRestoreVersion = async () => {
    if (!workspace?.id || !selectedVersionId || !versionHistory) return;

    const selectedVersion = versionHistory.versions.find(
      (v) => v.id === selectedVersionId,
    );
    if (!selectedVersion || selectedVersion.is_current) return;

    setRestoring(true);
    try {
      // For SharePoint-backed documents, the restore endpoint takes the main
      // doc ID (not the SP version string) in the path, plus sp_version_id as
      // a query param. For local docs the selected version ID is the MongoDB ID.
      const isSpVersion = !!selectedVersion.sp_version_id;
      const restoreDocId = isSpVersion
        ? (versionModalDoc?.id ?? selectedVersionId)
        : selectedVersionId;
      const result = await restoreVersion(
        workspace.id,
        restoreDocId,
        isSpVersion ? selectedVersion.sp_version_id : undefined,
      );
      setSuccess(
        `Restored version ${selectedVersion.version} as new version ${result.new_version}`,
      );
      handleCloseVersionModal();
      loadDocuments(); // Refresh document list
    } catch (err) {
      console.error("Failed to restore version:", err);
      setError(
        `Failed to restore: ${(err as any)?.response?.detail || (err as Error).message}`,
      );
    } finally {
      setRestoring(false);
    }
  };

  // Document editor handlers
  // Binary formats that cannot be edited as text
  const BINARY_EXTENSIONS = new Set([
    "pdf",
    "xlsx",
    "pptx",
    "png",
    "jpg",
    "jpeg",
    "gif",
    "webp",
  ]);
  const PREVIEWABLE_EXTENSIONS = new Set([
    "html",
    "md",
    "pdf",
    "png",
    "jpg",
    "jpeg",
    "gif",
    "webp",
    "txt",
    "csv",
    "json",
  ]);

  const isEditableFormat = (filename: string): boolean => {
    const ext = filename.toLowerCase().split(".").pop() || "";
    return ext !== "" && !BINARY_EXTENSIONS.has(ext);
  };

  const isPreviewableFormat = (filename: string): boolean => {
    const ext = filename.toLowerCase().split(".").pop() || "";
    return PREVIEWABLE_EXTENSIONS.has(ext);
  };

  const selectedVersion = useMemo(
    () =>
      versionHistory?.versions.find(
        (version) => version.id === selectedVersionId,
      ) ?? null,
    [selectedVersionId, versionHistory],
  );
  const currentVersion = useMemo(
    () =>
      versionHistory?.versions.find((version) => version.is_current) ?? null,
    [versionHistory],
  );

  const handleOpenPreview = (doc: IDADocument) => {
    setPreviewDocument(doc);
    setPreviewOpen(true);
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    setPreviewDocument(null);
    setPreviewSpVersionId(undefined);
  };

  // Preview the version selected in the version history window.
  const handlePreviewVersion = () => {
    if (!versionModalDoc || !selectedVersion) return;
    const spVersionId = selectedVersion.sp_version_id;
    setPreviewDocument({
      ...versionModalDoc,
      id: spVersionId ? versionModalDoc.id : selectedVersion.id,
      filename: selectedVersion.filename || versionModalDoc.filename,
    });
    setPreviewSpVersionId(spVersionId);
    setPreviewOpen(true);
  };

  const handleOpenEditor = async (doc: IDADocument) => {
    if (!workspace?.id) return;

    setEditorDocument(doc);
    setEditorLoading(true);
    setEditorOpen(true);

    try {
      const result = await getDocumentForEdit(workspace.id, doc.id);
      setEditorContent(result.full_text || "");
      setEditorOriginalFormat(result.original_format);
    } catch (err) {
      console.error("Failed to load document for edit:", err);
      setError(
        `Failed to load document: ${(err as any)?.response?.detail || (err as Error).message}`,
      );
      setEditorOpen(false);
    } finally {
      setEditorLoading(false);
    }
  };

  const handleCloseEditor = () => {
    setEditorOpen(false);
    setEditorDocument(null);
    setEditorContent("");
    setEditorOriginalFormat(undefined);
  };

  const handleSaveDocument = async (content: string) => {
    if (!workspace?.id || !editorDocument) return;

    setEditorSaving(true);
    try {
      const result = await saveDocumentContent(
        workspace.id,
        editorDocument.id,
        content,
      );
      setSuccess(`Document saved as version ${result.new_version}`);
      handleCloseEditor();
      loadDocuments(); // Refresh document list
    } catch (err) {
      console.error("Failed to save document:", err);
      const detail =
        (err as any)?.response?.detail ||
        (err as any)?.response?.detail ||
        (err as Error).message;
      setError(`Failed to save: ${detail}`);
    } finally {
      setEditorSaving(false);
    }
  };

  const fileExtension = (filename: string): string => {
    const dot = filename.lastIndexOf(".");
    return dot === -1 ? "" : filename.slice(dot).toLowerCase();
  };

  const handleOpenVersionUpload = (doc: IDADocument) => {
    const input = versionFileInputRef.current;
    if (!input) return;
    setVersionUploadTarget(doc);
    // The picker opens before React re-renders, so set the filter directly.
    input.accept = fileExtension(doc.filename);
    input.click();
  };

  const handleVersionFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    const doc = versionUploadTarget;
    setVersionUploadTarget(null);
    if (!file || !doc || !workspace?.id) return;

    const expected = fileExtension(doc.filename);
    if (fileExtension(file.name) !== expected) {
      setError(
        `A new version of ${doc.filename} must be a ${expected || "file without an extension"} file.`,
      );
      return;
    }
    if (!isAllowedFileSize(file)) {
      setError(
        `${file.name} must be larger than 0 bytes and at most ${formatFileSize(MAX_FILE_SIZE)}.`,
      );
      return;
    }

    setVersionUploadingId(doc.id);
    setError(null);
    try {
      const result = await uploadDocumentVersion(workspace.id, doc.id, file);
      setSuccess(`Uploaded ${doc.filename} as version ${result.new_version}`);
      loadDocuments();
    } catch (err) {
      const detail = (err as any)?.response?.detail || (err as Error).message;
      setError(`Failed to upload new version: ${detail}`);
    } finally {
      setVersionUploadingId(null);
    }
  };

  // Handle Download All (export all documents as ZIP)
  const handleDownloadAll = async () => {
    if (!workspace?.id || documents.length === 0) return;

    try {
      const result = await startExportZip(workspace.id, null);
      setExportJobs((prev) => [
        {
          job_id: result.job_id,
          status: "pending",
          progress: 0,
          doc_count: result.doc_count,
          created_at: new Date().toISOString(),
        },
        ...prev,
      ]);
      setSuccess(`Export started for ${result.doc_count} document(s)`);
    } catch (err) {
      console.error("Failed to start export:", err);
      setError(
        `Failed to start export: ${(err as any)?.response?.detail || (err as Error).message}`,
      );
    }
  };

  // Handle Download Selected (export selected documents as ZIP)
  const handleDownloadSelected = async () => {
    if (!workspace?.id || selectedIds.size === 0) return;

    const docIds = [...selectedIds];
    try {
      const result = await startExportZip(workspace.id, docIds);
      setExportJobs((prev) => [
        {
          job_id: result.job_id,
          status: "pending",
          progress: 0,
          doc_count: result.doc_count,
          created_at: new Date().toISOString(),
        },
        ...prev,
      ]);
      setSuccess(`Export started for ${result.doc_count} document(s)`);
    } catch (err) {
      console.error("Failed to start export:", err);
      setError(
        `Failed to start export: ${(err as any)?.response?.detail || (err as Error).message}`,
      );
    }
  };

  // Handle download of completed export
  const handleDownloadExport = async (job: ExportJob) => {
    if (!workspace?.id || downloadingExportId) return;

    setDownloadingExportId(job.job_id);
    try {
      await downloadExport(workspace.id, job.job_id);
    } catch (err) {
      console.error("Failed to download export:", err);
      setError(
        `Failed to download: ${(err as any)?.response?.detail || (err as Error).message}`,
      );
    } finally {
      setDownloadingExportId(null);
    }
  };

  // Handle dismiss export job
  const handleDismissExport = async (jobId: string) => {
    if (!workspace?.id) return;

    try {
      await dismissExportJob(workspace.id, jobId);
      setExportJobs((prev) => prev.filter((j) => j.job_id !== jobId));
    } catch (err) {
      console.error("Failed to dismiss export:", err);
    }
  };

  // Get all document IDs in a folder (recursively)
  const getDocIdsInFolder = useCallback((node: TreeNode): string[] => {
    const ids: string[] = [];
    if (node.type === "file" && node.document) {
      ids.push(node.document.id);
    }
    if (node.children) {
      for (const child of node.children) {
        ids.push(...getDocIdsInFolder(child));
      }
    }
    return ids;
  }, []);

  // Handle folder selection (select all documents in folder)
  const handleSelectFolder = (node: TreeNode, checked: boolean) => {
    const docIds = getDocIdsInFolder(node);
    setSelectedIds((prev) => {
      const newSet = new Set(prev);
      for (const id of docIds) {
        if (checked) {
          newSet.add(id);
        } else {
          newSet.delete(id);
        }
      }
      return newSet;
    });
  };

  // Check if folder is fully/partially selected
  const getFolderSelectionState = (node: TreeNode): "all" | "some" | "none" => {
    const docIds = getDocIdsInFolder(node);
    if (docIds.length === 0) return "none";
    const selectedCount = docIds.filter((id) => selectedIds.has(id)).length;
    if (selectedCount === 0) return "none";
    if (selectedCount === docIds.length) return "all";
    return "some";
  };

  // Get all documents in a folder (recursively) - defined first so it can be used by getFolderIndexStatus
  const getDocsInFolder = (node: TreeNode): IDADocument[] => {
    const docs: IDADocument[] = [];
    if (node.type === "file" && node.document) {
      docs.push(node.document);
    }
    if (node.children) {
      for (const child of node.children) {
        docs.push(...getDocsInFolder(child));
      }
    }
    return docs;
  };

  // Get folder indexing status for collapsed view
  const getFolderIndexStatus = (
    node: TreeNode,
  ): "completed" | "processing" | "pending" => {
    const docs = getDocsInFolder(node);
    if (docs.length === 0) return "completed";

    // Any syncing from SharePoint?
    const hasSyncing = docs.some((d) => d.index_status === "syncing");
    if (hasSyncing) return "processing";

    // Any still processing?
    const hasProcessing = docs.some((d) => d.index_status === "processing");
    if (hasProcessing) return "processing";

    // Any still pending?
    const hasPending = docs.some((d) => d.index_status === "pending");
    if (hasPending) return "pending";

    // All done (completed or failed)
    return "completed";
  };

  // Handle individual selection
  const handleSelectOne = (docId: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(docId);
      } else {
        newSet.delete(docId);
      }
      return newSet;
    });
  };

  // Handle select all
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(new Set(documents.map((d) => d.id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  // Get overall selection state for header checkbox
  const getAllSelectionState = (): "none" | "some" | "all" => {
    if (documents.length === 0) return "none";
    if (selectedIds.size === 0) return "none";
    if (selectedIds.size === documents.length) return "all";
    return "some";
  };

  // Toggle folder expansion
  const toggleFolder = (path: string) => {
    setExpandedFolders((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(path)) {
        newSet.delete(path);
      } else {
        newSet.add(path);
      }
      return newSet;
    });
  };

  // Get icon for file type
  const getFileIcon = (filename: string) => {
    const ext = filename.split(".").pop()?.toLowerCase();
    switch (ext) {
      case "pdf":
        return <PictureAsPdfIcon sx={{ color: "danger.500", fontSize: 20 }} />;
      case "doc":
      case "docx":
        return <ArticleIcon sx={{ color: "primary.500", fontSize: 20 }} />;
      case "xls":
      case "xlsx":
      case "csv":
        return <TableChartIcon sx={{ color: "success.600", fontSize: 20 }} />;
      case "ppt":
      case "pptx":
        return <SlideshowIcon sx={{ color: "warning.600", fontSize: 20 }} />;
      case "png":
      case "jpg":
      case "jpeg":
      case "gif":
      case "svg":
      case "webp":
      case "bmp":
      case "tiff":
      case "ico":
        return <ImageIcon sx={{ color: "primary.400", fontSize: 20 }} />;
      case "mp3":
      case "wav":
      case "ogg":
      case "flac":
      case "aac":
      case "m4a":
        return <AudioFileIcon sx={{ color: "warning.500", fontSize: 20 }} />;
      case "mp4":
      case "mov":
      case "avi":
      case "mkv":
      case "webm":
        return <VideoFileIcon sx={{ color: "primary.600", fontSize: 20 }} />;
      case "json":
      case "xml":
      case "yaml":
      case "yml":
        return <DataObjectIcon sx={{ color: "neutral.600", fontSize: 20 }} />;
      case "html":
      case "css":
      case "js":
      case "ts":
      case "py":
      case "java":
      case "cpp":
      case "c":
      case "rb":
      case "go":
      case "rs":
      case "sh":
        return <CodeIcon sx={{ color: "neutral.600", fontSize: 20 }} />;
      case "txt":
      case "md":
      case "rtf":
      case "log":
        return <TextSnippetIcon sx={{ color: "neutral.500", fontSize: 20 }} />;
      default:
        return <DescriptionIcon sx={{ color: "neutral.500", fontSize: 20 }} />;
    }
  };

  // Format file size
  const formatSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Format date
  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    return date.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Drag and drop handlers
  const handleDragStart = useCallback(
    (
      e: React.DragEvent,
      data: { type: "file" | "folder"; docId?: string; folderPath?: string },
    ) => {
      e.dataTransfer.setData("application/json", JSON.stringify(data));
      e.dataTransfer.effectAllowed = "move";
    },
    [],
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent, folderPath: string) => {
      // External file drops show "copy" cursor, internal moves show "move"
      const hasFiles = e.dataTransfer.types.includes("Files");
      if (hasFiles && (persistentExceeded || uploading)) {
        e.dataTransfer.dropEffect = "none";
        return;
      }
      e.preventDefault();
      e.dataTransfer.dropEffect = hasFiles ? "copy" : "move";
      setDragOverFolder(folderPath);
    },
    [persistentExceeded, uploading],
  );

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    // Only clear if we're leaving the folder row entirely
    const relatedTarget = e.relatedTarget as HTMLElement | null;
    if (!relatedTarget || !e.currentTarget.contains(relatedTarget)) {
      setDragOverFolder(null);
    }
  }, []);

  // Handle external file drop (OS files dragged into the browser)
  const handleExternalFileDrop = useCallback(
    async (files: FileList, targetFolder: string) => {
      if (!workspace?.id || files.length === 0) return;
      if (persistentExceeded) {
        setError(
          "Storage quota exceeded. Upgrade your plan to upload more files.",
        );
        return;
      }
      if (uploading) return;

      const fileArray = Array.from(files);
      const allowedFiles = fileArray.filter(
        (f) => isAllowedFile(f.name) && isAllowedFileSize(f),
      );
      const skippedCount = fileArray.length - allowedFiles.length;

      if (allowedFiles.length === 0) {
        setError(
          `No uploadable files. Supported: ${DOCUMENT_UPLOAD_ALLOWED_EXTENSIONS.join(", ")}, max 10MB`,
        );
        return;
      }

      setUploading(true);
      setError(null);

      try {
        const BATCH_SIZE = 10;
        let completed = 0;
        const failedUploads: string[] = [];

        for (let i = 0; i < allowedFiles.length; i += BATCH_SIZE) {
          const batch = allowedFiles.slice(i, i + BATCH_SIZE);
          setUploadProgress(
            `Uploading ${completed + 1}-${Math.min(completed + batch.length, allowedFiles.length)} of ${allowedFiles.length}...`,
          );

          const results = await Promise.allSettled(
            batch.map((file) =>
              uploadDocument(workspace.id, file, targetFolder),
            ),
          );

          results.forEach((result, idx) => {
            if (result.status === "rejected") {
              failedUploads.push(batch[idx].name);
            }
          });

          completed += batch.length;
        }

        setUploadProgress(null);
        loadDocuments();

        const uploadedCount = allowedFiles.length - failedUploads.length;
        if (uploadedCount > 0) {
          const folderLabel = targetFolder === "/" ? "root" : targetFolder;
          setSuccess(
            `Uploaded ${uploadedCount} file${uploadedCount !== 1 ? "s" : ""} to ${folderLabel}`,
          );
        }

        const errors: string[] = [];
        if (skippedCount > 0) {
          errors.push(
            `${skippedCount} file(s) skipped (unsupported format or too large)`,
          );
        }
        if (failedUploads.length > 0) {
          errors.push(`Failed: ${failedUploads.join(", ")}`);
        }
        if (errors.length > 0) {
          setError(errors.join(". "));
        }
      } catch (err) {
        setError(`Upload failed: ${(err as Error).message}`);
      } finally {
        setUploading(false);
        setUploadProgress(null);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      workspace?.id,
      uploadDocument,
      loadDocuments,
      persistentExceeded,
      uploading,
    ],
  );

  const handleDrop = useCallback(
    async (e: React.DragEvent, targetFolder: string) => {
      e.preventDefault();
      setDragOverFolder(null);

      // External file drop from OS
      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        // Check if this is truly an external drop (no internal JSON data)
        let hasInternalData = false;
        try {
          const jsonData = e.dataTransfer.getData("application/json");
          if (jsonData) hasInternalData = true;
        } catch {
          // No internal data
        }

        if (!hasInternalData) {
          handleExternalFileDrop(e.dataTransfer.files, targetFolder);
          return;
        }
      }

      let data: { type: string; docId?: string; folderPath?: string };
      try {
        data = JSON.parse(e.dataTransfer.getData("application/json"));
      } catch {
        return;
      }

      if (!workspace) return;

      if (data.type === "file" && data.docId) {
        // Don't drop a file onto the folder it's already in
        const doc = documents.find((d) => d.id === data.docId);
        if (doc && (doc.folder || "/") === targetFolder) return;

        try {
          await moveDocuments(workspace.id, [data.docId], targetFolder);
          setSuccess(
            `Moved file to ${targetFolder === "/" ? "root" : targetFolder}`,
          );
          loadDocuments();
        } catch {
          setError("Failed to move file");
        }
      } else if (data.type === "folder" && data.folderPath) {
        // Don't drop a folder onto itself or its own parent
        if (data.folderPath === targetFolder) return;
        if (targetFolder.startsWith(data.folderPath + "/")) return;

        // Move all documents from the source folder to targetFolder/folderName
        const folderName = data.folderPath.split("/").filter(Boolean).pop();
        const newPath =
          targetFolder === "/"
            ? `/${folderName}`
            : `${targetFolder}/${folderName}`;
        const docsInFolder = documents.filter(
          (d) =>
            (d.folder || "/") === data.folderPath ||
            (d.folder || "/").startsWith(data.folderPath + "/"),
        );

        if (docsInFolder.length === 0) return;

        try {
          // Move each document, updating folder path prefix
          const movePromises = docsInFolder.map((doc) => {
            const currentFolder = doc.folder || "/";
            const newFolder =
              currentFolder === data.folderPath
                ? newPath
                : newPath + currentFolder.slice(data.folderPath!.length);
            return moveDocuments(workspace.id, [doc.id], newFolder);
          });
          await Promise.all(movePromises);
          setSuccess(
            `Moved folder to ${targetFolder === "/" ? "root" : targetFolder}`,
          );
          loadDocuments();
        } catch {
          setError("Failed to move folder");
        }
      }
    },
    [
      workspace,
      documents,
      moveDocuments,
      loadDocuments,
      handleExternalFileDrop,
    ],
  );

  // Render tree node recursively
  const renderTreeNode = (
    node: TreeNode,
    depth: number = 0,
  ): React.ReactNode => {
    const isExpanded = expandedFolders.has(node.path);
    const indent = depth * 24;

    if (node.type === "folder") {
      const selectionState = getFolderSelectionState(node);
      const docIds = getDocIdsInFolder(node);
      const docCount = docIds.length;
      const deletingCount = docIds.filter((id) => deletingIds.has(id)).length;
      const isDeleting = deletingCount > 0;
      const isHovered = hoveredFolder === node.path;

      // Don't render root folder as a row, just render its children
      if (node.path === "/") {
        return (
          <>
            {node.children?.map((child) => (
              <React.Fragment key={child.path}>
                {renderTreeNode(child, depth)}
              </React.Fragment>
            ))}
          </>
        );
      }

      const isDragOver = dragOverFolder === node.path;
      const canDragFolder = folderCount > 1;

      return (
        <React.Fragment key={node.path}>
          {/* Folder row */}
          <Box
            onMouseEnter={() => setHoveredFolder(node.path)}
            onMouseLeave={() => setHoveredFolder(null)}
            onDragOver={(e) => handleDragOver(e, node.path)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, node.path)}
            draggable={canDragFolder}
            onDragStart={
              canDragFolder
                ? (e) =>
                    handleDragStart(e, {
                      type: "folder",
                      folderPath: node.path,
                    })
                : undefined
            }
            sx={{
              display: "flex",
              alignItems: "center",
              minWidth: DOCUMENT_TABLE_MIN_WIDTH,
              py: 1,
              px: 1,
              pl: `${indent + 8}px`,
              borderBottom: "1px solid",
              borderColor: isDragOver ? "primary.500" : "divider",
              bgcolor: isDragOver
                ? "primary.softBg"
                : isDeleting
                  ? "danger.softBg"
                  : "background.level1",
              "&:hover": {
                bgcolor: isDeleting ? "danger.softBg" : "background.level2",
              },
              opacity: isDeleting ? 0.7 : 1,
              outline: isDragOver ? "2px solid" : "none",
              outlineColor: isDragOver ? "primary.500" : "transparent",
              transition: "background-color 0.15s, outline 0.15s",
            }}
          >
            {canDragFolder && (
              <DragIndicatorIcon
                sx={{
                  fontSize: 18,
                  color: "neutral.400",
                  cursor: "grab",
                  mr: 0.5,
                  "&:active": { cursor: "grabbing" },
                }}
              />
            )}
            <Checkbox
              size="sm"
              checked={selectionState === "all"}
              indeterminate={selectionState === "some"}
              onChange={(e) => handleSelectFolder(node, e.target.checked)}
              disabled={isDeleting}
              sx={{ mr: 1 }}
            />
            <IconButton
              size="sm"
              variant="plain"
              onClick={() => toggleFolder(node.path)}
              sx={{ mr: 0.5 }}
            >
              {isExpanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}
            </IconButton>
            {isDeleting ? (
              <CircularProgress size="sm" sx={{ mr: 1 }} />
            ) : isExpanded ? (
              <FolderOpenIcon
                sx={{ color: "warning.500", fontSize: 20, mr: 1 }}
              />
            ) : (
              <FolderIcon sx={{ color: "warning.500", fontSize: 20, mr: 1 }} />
            )}
            <Typography
              level="body-sm"
              fontWeight="md"
              sx={{ cursor: "pointer", flex: 1 }}
              onClick={() => toggleFolder(node.path)}
            >
              {node.name}
            </Typography>

            {/* Hover actions or file count + status */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                minWidth: 200,
                justifyContent: "flex-end",
                gap: 1,
              }}
            >
              {isHovered && !isDeleting ? (
                <Stack direction="row" spacing={0.5}>
                  <Tooltip
                    title={
                      persistentExceeded
                        ? "Storage quota exceeded. Upgrade your plan to upload more files."
                        : "Upload files"
                    }
                  >
                    <span>
                      <IconButton
                        size="sm"
                        variant="soft"
                        color="primary"
                        disabled={persistentExceeded}
                        onClick={(e) => {
                          e.stopPropagation();
                          triggerFileUpload(node.path);
                        }}
                      >
                        <CloudUploadIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip
                    title={
                      persistentExceeded
                        ? "Storage quota exceeded. Upgrade your plan to upload more files."
                        : "Upload folder"
                    }
                  >
                    <span>
                      <IconButton
                        size="sm"
                        variant="soft"
                        color="primary"
                        disabled={persistentExceeded}
                        onClick={(e) => {
                          e.stopPropagation();
                          triggerFolderUpload(node.path);
                        }}
                      >
                        <DriveFolderUploadIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Create subfolder">
                    <IconButton
                      size="sm"
                      variant="soft"
                      color="neutral"
                      onClick={(e) => {
                        e.stopPropagation();
                        openCreateFolderModal(node.path);
                      }}
                    >
                      <CreateNewFolderIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete folder">
                    <IconButton
                      size="sm"
                      variant="soft"
                      color="danger"
                      onClick={(e) => {
                        e.stopPropagation();
                        setFolderDeleteConfirm(node);
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              ) : (
                <>
                  <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
                    {isDeleting
                      ? `Deleting ${deletingCount}/${docCount}...`
                      : `${docCount} file${docCount !== 1 ? "s" : ""}`}
                  </Typography>
                  {/* Show status icon only when collapsed */}
                  {!isExpanded &&
                    !isDeleting &&
                    (() => {
                      const status = getFolderIndexStatus(node);
                      if (status === "completed") {
                        return (
                          <Tooltip title="Indexed">
                            <CheckCircleIcon
                              sx={{ color: "success.500", fontSize: 18 }}
                            />
                          </Tooltip>
                        );
                      } else if (status === "processing") {
                        return (
                          <Tooltip title="Processing">
                            <CircularProgress
                              size="sm"
                              thickness={3}
                              sx={{ "--CircularProgress-size": "18px" }}
                            />
                          </Tooltip>
                        );
                      } else {
                        return (
                          <Tooltip title="Pending">
                            <HourglassEmptyIcon
                              sx={{ color: "warning.500", fontSize: 18 }}
                            />
                          </Tooltip>
                        );
                      }
                    })()}
                </>
              )}
            </Box>
          </Box>

          {/* Children (if expanded) */}
          {isExpanded &&
            node.children?.map((child) => (
              <React.Fragment key={child.path}>
                {renderTreeNode(child, depth + 1)}
              </React.Fragment>
            ))}
        </React.Fragment>
      );
    }

    // File row
    const doc = node.document!;
    const isDownloading = downloadingId === doc.id;
    const fileFolder = doc.folder || "/";
    const isFileDragOver = dragOverFolder === `file:${doc.id}`;
    return (
      <Box
        key={node.path}
        draggable={hasFolders}
        onDragStart={
          hasFolders
            ? (e) => handleDragStart(e, { type: "file", docId: doc.id })
            : undefined
        }
        onDragOver={(e) => {
          // Only accept external file drops on file rows
          if (
            e.dataTransfer.types.includes("Files") &&
            !persistentExceeded &&
            !uploading
          ) {
            e.preventDefault();
            e.dataTransfer.dropEffect = "copy";
            setDragOverFolder(`file:${doc.id}`);
          }
        }}
        onDragLeave={(e) => {
          const relatedTarget = e.relatedTarget as HTMLElement | null;
          if (!relatedTarget || !e.currentTarget.contains(relatedTarget)) {
            setDragOverFolder(null);
          }
        }}
        onDrop={(e) => {
          // Only handle external file drops; internal moves go to folder targets
          if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            let hasInternalData = false;
            try {
              if (e.dataTransfer.getData("application/json"))
                hasInternalData = true;
            } catch {
              /* no internal data */
            }

            if (!hasInternalData) {
              e.preventDefault();
              setDragOverFolder(null);
              handleExternalFileDrop(e.dataTransfer.files, fileFolder);
              return;
            }
          }
        }}
        sx={{
          display: "flex",
          alignItems: "center",
          minWidth: DOCUMENT_TABLE_MIN_WIDTH,
          py: 1,
          px: 1,
          pl: `${indent + 8}px`,
          borderBottom: "1px solid",
          borderColor: isFileDragOver ? "primary.500" : "divider",
          bgcolor: isFileDragOver ? "primary.softBg" : undefined,
          "&:hover": {
            bgcolor: isFileDragOver ? "primary.softBg" : "background.level1",
          },
          outline: isFileDragOver ? "2px solid" : "none",
          outlineColor: isFileDragOver ? "primary.500" : "transparent",
          transition: "background-color 0.15s, outline 0.15s",
        }}
      >
        {hasFolders && (
          <DragIndicatorIcon
            sx={{
              fontSize: 18,
              color: "neutral.400",
              cursor: "grab",
              mr: 0.5,
              "&:active": { cursor: "grabbing" },
            }}
          />
        )}
        <Checkbox
          size="sm"
          checked={selectedIds.has(doc.id)}
          onChange={(e) => handleSelectOne(doc.id, e.target.checked)}
          sx={{ mr: 1 }}
        />
        <Box sx={{ width: 28 }} /> {/* Spacer for alignment */}
        <Box sx={{ mr: 1 }}>{getFileIcon(doc.filename)}</Box>
        {isDownloading ? (
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ flex: 1, minWidth: 0 }}
          >
            <CircularProgress size="sm" />
            <Typography level="body-sm" sx={{ color: "text.tertiary" }}>
              Downloading...
            </Typography>
          </Stack>
        ) : (
          <Tooltip title={doc.filename} placement="top-start" enterDelay={500}>
            <Typography
              level="body-sm"
              sx={{
                flex: "1 1 auto",
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                cursor: "pointer",
                color: "primary.plainColor",
                textDecoration: "none",
                "&:hover": { textDecoration: "underline" },
              }}
              noWrap
              onClick={() =>
                isPreviewableFormat(doc.filename)
                  ? handleOpenPreview(doc)
                  : handleDownload(doc)
              }
            >
              {doc.filename}
            </Typography>
          </Tooltip>
        )}
        <Typography
          level="body-xs"
          sx={{
            color: "text.tertiary",
            width: 70,
            flexShrink: 0,
            textAlign: "right",
          }}
        >
          {formatSize(doc.file_size)}
        </Typography>
        <Typography
          level="body-xs"
          sx={{
            color: "text.tertiary",
            width: 70,
            flexShrink: 0,
            textAlign: "right",
          }}
        >
          {doc.token_count.toLocaleString()}
        </Typography>
        <Typography
          level="body-xs"
          sx={{
            color: "text.tertiary",
            width: 50,
            flexShrink: 0,
            textAlign: "right",
          }}
        >
          {doc.page_count || "-"}
        </Typography>
        <Typography
          level="body-xs"
          sx={{
            color: "text.tertiary",
            width: 120,
            flexShrink: 0,
            textAlign: "right",
            whiteSpace: "nowrap",
          }}
        >
          {formatDate(doc.uploaded_at)}
        </Typography>
        <Box
          sx={{
            width: 50,
            flexShrink: 0,
            display: "flex",
            justifyContent: "center",
          }}
        >
          {doc.index_status === "completed" ? (
            <Tooltip title="Indexed">
              <CheckCircleIcon sx={{ color: "success.500", fontSize: 20 }} />
            </Tooltip>
          ) : doc.index_status === "processing" ? (
            <Tooltip title="Processing">
              <CircularProgress
                size="sm"
                thickness={3}
                sx={{ "--CircularProgress-size": "20px" }}
              />
            </Tooltip>
          ) : doc.index_status === "syncing" ? (
            <Tooltip title="Syncing from SharePoint">
              <CircularProgress
                size="sm"
                thickness={3}
                sx={{ "--CircularProgress-size": "20px", color: "primary.500" }}
              />
            </Tooltip>
          ) : doc.index_status === "failed" ? (
            <Tooltip title={doc.error_message || "Indexing failed"}>
              <ErrorIcon sx={{ color: "danger.500", fontSize: 20 }} />
            </Tooltip>
          ) : doc.index_status === "empty" ? (
            // #598: processed but produced no searchable content -- distinct
            // from "completed" (which implies searchable success). The reason
            // (error_message) is surfaced so the user knows WHY it is empty,
            // not just that the status changed.
            <Tooltip
              title={`Empty: ${
                doc.error_message || "no searchable content was extracted"
              }`}
            >
              <WarningAmberIcon sx={{ color: "warning.500", fontSize: 20 }} />
            </Tooltip>
          ) : (
            <Tooltip title="Pending">
              <HourglassEmptyIcon sx={{ color: "warning.500", fontSize: 20 }} />
            </Tooltip>
          )}
        </Box>
        {/* SharePoint backend badge */}
        {doc.backend === "sharepoint" && (
          <Box sx={{ width: 30, display: "flex", justifyContent: "center" }}>
            <Tooltip title="Stored in SharePoint">
              <CloudSyncIcon sx={{ color: "primary.500", fontSize: 18 }} />
            </Tooltip>
          </Box>
        )}
        {/* Version indicator */}
        <Box sx={{ width: 50, display: "flex", justifyContent: "center" }}>
          <Tooltip title="View version history">
            <Chip
              size="sm"
              variant="soft"
              color={doc.version && doc.version > 1 ? "primary" : "neutral"}
              startDecorator={<HistoryIcon sx={{ fontSize: 14 }} />}
              onClick={() => handleOpenVersionModal(doc)}
              sx={{ cursor: "pointer" }}
            >
              v{doc.version || 1}
            </Chip>
          </Tooltip>
        </Box>
        {/* Access/publish indicator */}
        <Box
          sx={{
            width: 40,
            flexShrink: 0,
            display: "flex",
            justifyContent: "center",
          }}
        >
          {doc.is_public ? (
            <Tooltip title="Published (Public)">
              <PublicIcon sx={{ color: "success.500", fontSize: 18 }} />
            </Tooltip>
          ) : doc.is_published ? (
            <Tooltip title="Published (Org)">
              <BusinessIcon sx={{ color: "primary.500", fontSize: 18 }} />
            </Tooltip>
          ) : (
            <Tooltip title="Private">
              <LockIcon sx={{ color: "neutral.400", fontSize: 18 }} />
            </Tooltip>
          )}
        </Box>
        {/* Preview button for previewable formats */}
        <Box
          sx={{
            width: 40,
            flexShrink: 0,
            display: "flex",
            justifyContent: "center",
          }}
        >
          {isPreviewableFormat(doc.filename) && (
            <Tooltip title="Preview">
              <IconButton
                size="sm"
                variant="plain"
                color="neutral"
                onClick={() => handleOpenPreview(doc)}
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        {/* Upload a new version of this document */}
        <Box sx={{ width: 40, display: "flex", justifyContent: "center" }}>
          {versionUploadingId === doc.id ? (
            <CircularProgress size="sm" />
          ) : (
            <Tooltip
              title={
                persistentExceeded
                  ? "Storage limit reached"
                  : "Upload new version"
              }
            >
              <span>
                <IconButton
                  size="sm"
                  variant="plain"
                  color="neutral"
                  aria-label={`Upload new version of ${doc.filename}`}
                  disabled={persistentExceeded || versionUploadingId !== null}
                  onClick={() => handleOpenVersionUpload(doc)}
                >
                  <UploadFileIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Box>
        {/* Edit button for editable formats */}
        <Box
          sx={{
            width: 40,
            flexShrink: 0,
            display: "flex",
            justifyContent: "center",
          }}
        >
          {isEditableFormat(doc.filename) && (
            <Tooltip title="Edit document">
              <IconButton
                size="sm"
                variant="plain"
                color="primary"
                onClick={() => handleOpenEditor(doc)}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        <Box
          sx={{
            width: 40,
            flexShrink: 0,
            display: "flex",
            justifyContent: "center",
          }}
        >
          {deletingIds.has(doc.id) ? (
            <CircularProgress size="sm" />
          ) : (
            <Tooltip title="Delete document">
              <IconButton
                size="sm"
                variant="plain"
                color="danger"
                onClick={() => setDeleteConfirm(doc)}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <Page>
      <PageHeader>
        <Typography level="h1">IDA Documents</Typography>
        <Stack direction="row" spacing={1}>
          {selectedIds.size > 0 && (
            <>
              <Button
                startDecorator={<DownloadIcon />}
                variant="soft"
                color="primary"
                onClick={handleDownloadSelected}
              >
                Download Selected ({selectedIds.size})
              </Button>
              <Button
                startDecorator={<DeleteIcon />}
                variant="soft"
                color="danger"
                onClick={() => setBatchDeleteConfirm(true)}
              >
                Delete Selected ({selectedIds.size})
              </Button>
            </>
          )}
          <Tooltip title="Refresh">
            <IconButton
              variant="plain"
              color="neutral"
              onClick={() => loadDocuments()}
              disabled={loading}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          {/* Show upload buttons at top only when there are no folders */}
          {!hasFolders && (
            <>
              {documents.length > 0 && (
                <Tooltip title="Download all as ZIP">
                  <IconButton
                    variant="soft"
                    color="primary"
                    onClick={handleDownloadAll}
                  >
                    <FolderZipIcon />
                  </IconButton>
                </Tooltip>
              )}
              {/* Manual SharePoint sync button — shown while syncing or after initial sync */}
              {spSyncing && (
                <Tooltip title="Syncing files from SharePoint...">
                  <Button
                    variant="soft"
                    color="primary"
                    startDecorator={<CloudSyncIcon />}
                    loading={true}
                    disabled
                  >
                    Syncing SharePoint
                  </Button>
                </Tooltip>
              )}
              <Tooltip
                title={
                  persistentExceeded
                    ? "Storage quota exceeded. Upgrade your plan to upload more files."
                    : ""
                }
                placement="top"
              >
                <span>
                  <Button
                    startDecorator={<CloudUploadIcon />}
                    variant="solid"
                    color="primary"
                    onClick={() => triggerFileUpload("/")}
                    loading={uploading}
                    disabled={persistentExceeded}
                  >
                    Upload Files
                  </Button>
                </span>
              </Tooltip>
              <Tooltip
                title={
                  persistentExceeded
                    ? "Storage quota exceeded. Upgrade your plan to upload more files."
                    : ""
                }
                placement="top"
              >
                <span>
                  <Button
                    startDecorator={<DriveFolderUploadIcon />}
                    variant="soft"
                    color="primary"
                    onClick={() => triggerFolderUpload("/")}
                    disabled={uploading || persistentExceeded}
                  >
                    Upload Folder
                  </Button>
                </span>
              </Tooltip>
              <Button
                startDecorator={<CreateNewFolderIcon />}
                variant="soft"
                color="neutral"
                onClick={() => openCreateFolderModal("/")}
                disabled={uploading}
              >
                Create Folder
              </Button>
            </>
          )}
          {/* Hidden file input for individual file selection */}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={DOCUMENT_UPLOAD_ALLOWED_EXTENSIONS.join(",")}
            style={{ display: "none" }}
            onChange={handleFileSelect}
          />
          {/* Hidden file input for folder selection */}
          <input
            ref={folderInputRef}
            type="file"
            multiple
            // @ts-expect-error webkitdirectory is a non-standard attribute
            webkitdirectory=""
            directory=""
            style={{ display: "none" }}
            onChange={handleFileSelect}
          />
          {/* Hidden file input for uploading a new version of one document */}
          <input
            ref={versionFileInputRef}
            type="file"
            data-testid="version-file-input"
            style={{ display: "none" }}
            onChange={handleVersionFileSelect}
          />
        </Stack>
      </PageHeader>

      <Stack spacing={3}>
        {/* Upload progress */}
        {uploadProgress && (
          <Alert color="primary" variant="soft">
            <Stack spacing={1} sx={{ width: "100%" }}>
              <Typography level="body-sm">{uploadProgress}</Typography>
              <LinearProgress />
            </Stack>
          </Alert>
        )}

        {/* Success */}
        {success && (
          <Alert
            color="success"
            variant="soft"
            startDecorator={<CheckCircleIcon />}
            endDecorator={
              <IconButton
                variant="plain"
                size="sm"
                color="success"
                onClick={() => setSuccess(null)}
              >
                <CloseIcon />
              </IconButton>
            }
          >
            {success}
          </Alert>
        )}

        {/* Error */}
        {error && (
          <Alert
            color="danger"
            variant="soft"
            endDecorator={
              <IconButton
                variant="plain"
                size="sm"
                color="danger"
                onClick={() => setError(null)}
              >
                <CloseIcon />
              </IconButton>
            }
          >
            {error}
          </Alert>
        )}

        {/* SharePoint sync error */}
        {spSyncErrors.length > 0 && (
          <Alert
            color="warning"
            variant="soft"
            endDecorator={
              <IconButton
                variant="plain"
                size="sm"
                color="warning"
                onClick={() => setSpSyncErrors([])}
              >
                <CloseIcon />
              </IconButton>
            }
          >
            SharePoint sync failed. Please contact your support team.
          </Alert>
        )}

        {/* Export Jobs Status Panel */}
        {exportJobs.length > 0 && (
          <Card variant="soft" sx={{ mb: 0 }}>
            <CardContent sx={{ py: 1.5 }}>
              <Typography level="title-sm" sx={{ mb: 1 }}>
                <FolderZipIcon
                  sx={{ fontSize: 18, mr: 0.5, verticalAlign: "text-bottom" }}
                />
                Downloads
              </Typography>
              <Stack spacing={1}>
                {exportJobs.map((job) => (
                  <Box
                    key={job.job_id}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      py: 0.5,
                    }}
                  >
                    {/* Status icon */}
                    {job.status === "pending" && (
                      <HourglassEmptyIcon
                        sx={{ color: "warning.500", fontSize: 20 }}
                      />
                    )}
                    {job.status === "processing" && (
                      <CircularProgress
                        size="sm"
                        thickness={3}
                        sx={{ "--CircularProgress-size": "20px" }}
                      />
                    )}
                    {job.status === "complete" && (
                      <CheckCircleIcon
                        sx={{ color: "success.500", fontSize: 20 }}
                      />
                    )}
                    {job.status === "failed" && (
                      <ErrorIcon sx={{ color: "danger.500", fontSize: 20 }} />
                    )}

                    {/* Status text */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      {job.status === "pending" && (
                        <Typography level="body-sm">
                          Preparing {job.doc_count} document
                          {job.doc_count !== 1 ? "s" : ""}...
                        </Typography>
                      )}
                      {job.status === "processing" && (
                        <Stack spacing={0.5}>
                          <Typography level="body-sm">
                            Creating ZIP: {job.doc_count} document
                            {job.doc_count !== 1 ? "s" : ""} ({job.progress}%)
                          </Typography>
                          <LinearProgress
                            determinate
                            value={job.progress}
                            sx={{ height: 4, borderRadius: 2 }}
                          />
                        </Stack>
                      )}
                      {job.status === "complete" && (
                        <Typography level="body-sm">
                          {job.doc_count} document
                          {job.doc_count !== 1 ? "s" : ""} ready for download
                        </Typography>
                      )}
                      {job.status === "failed" && (
                        <Typography
                          level="body-sm"
                          sx={{ color: "danger.plainColor" }}
                        >
                          Export failed: {job.error || "Unknown error"}
                        </Typography>
                      )}
                    </Box>

                    {/* Actions */}
                    {job.status === "complete" && (
                      <Button
                        size="sm"
                        variant="solid"
                        color="primary"
                        startDecorator={
                          downloadingExportId === job.job_id ? (
                            <CircularProgress size="sm" />
                          ) : (
                            <DownloadIcon />
                          )
                        }
                        onClick={() => handleDownloadExport(job)}
                        disabled={downloadingExportId === job.job_id}
                      >
                        Download ZIP
                      </Button>
                    )}

                    {/* Dismiss button */}
                    <Tooltip title="Dismiss">
                      <IconButton
                        size="sm"
                        variant="plain"
                        color="neutral"
                        onClick={() => handleDismissExport(job.job_id)}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ))}
              </Stack>
            </CardContent>
          </Card>
        )}

        {/* Documents tree */}
        <Card>
          <CardContent sx={{ p: 0 }}>
            {loading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  py: 6,
                }}
              >
                <CircularProgress />
              </Box>
            ) : documents.length === 0 && persistedFolders.length === 0 ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  py: 6,
                }}
              >
                <DescriptionIcon
                  sx={{ fontSize: 64, color: "neutral.400", mb: 2 }}
                />
                <Typography level="h3" sx={{ mb: 1 }}>
                  No Documents Yet
                </Typography>
                <Typography
                  level="body-md"
                  sx={{ mb: 2, color: "text.secondary", textAlign: "center" }}
                >
                  Upload documents to start using intelligent search and
                  retrieval.
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Tooltip
                    title={
                      persistentExceeded
                        ? "Storage quota exceeded. Upgrade your plan to upload more files."
                        : ""
                    }
                    placement="top"
                  >
                    <span>
                      <Button
                        startDecorator={<CloudUploadIcon />}
                        variant="soft"
                        color="primary"
                        onClick={() => triggerFileUpload("/")}
                        disabled={persistentExceeded}
                      >
                        Upload Files
                      </Button>
                    </span>
                  </Tooltip>
                  <Tooltip
                    title={
                      persistentExceeded
                        ? "Storage quota exceeded. Upgrade your plan to upload more files."
                        : ""
                    }
                    placement="top"
                  >
                    <span>
                      <Button
                        startDecorator={<DriveFolderUploadIcon />}
                        variant="soft"
                        color="primary"
                        onClick={() => triggerFolderUpload("/")}
                        disabled={persistentExceeded}
                      >
                        Upload Folder
                      </Button>
                    </span>
                  </Tooltip>
                  <Button
                    startDecorator={<CreateNewFolderIcon />}
                    variant="plain"
                    color="neutral"
                    onClick={() => openCreateFolderModal("/")}
                  >
                    Create Folder
                  </Button>
                </Stack>
              </Box>
            ) : (
              <Box sx={{ overflow: "auto" }}>
                {/* Header */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    minWidth: DOCUMENT_TABLE_MIN_WIDTH,
                    py: 1,
                    px: 1,
                    borderBottom: "2px solid",
                    borderColor: "divider",
                    bgcolor: "background.level2",
                  }}
                >
                  {hasFolders && <Box sx={{ width: 22 }} />}{" "}
                  {/* Drag handle spacer */}
                  <Checkbox
                    size="sm"
                    checked={getAllSelectionState() === "all"}
                    indeterminate={getAllSelectionState() === "some"}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    disabled={documents.length === 0}
                    sx={{ mr: 1 }}
                  />
                  <Box sx={{ width: 28 }} /> {/* Expand icon spacer */}
                  <Box sx={{ width: 28 }} /> {/* File icon spacer */}
                  <DocumentSortHeader
                    label="Name"
                    column="name"
                    sortState={documentSort}
                    onSort={handleDocumentSort}
                    flex={1}
                  />
                  <DocumentSortHeader
                    label="Size"
                    column="size"
                    sortState={documentSort}
                    onSort={handleDocumentSort}
                    width={70}
                    align="right"
                  />
                  <DocumentSortHeader
                    label="Tokens"
                    column="tokens"
                    sortState={documentSort}
                    onSort={handleDocumentSort}
                    width={70}
                    align="right"
                  />
                  <DocumentSortHeader
                    label="Pages"
                    column="pages"
                    sortState={documentSort}
                    onSort={handleDocumentSort}
                    width={50}
                    align="right"
                  />
                  <DocumentSortHeader
                    label="Uploaded"
                    column="uploaded"
                    sortState={documentSort}
                    onSort={handleDocumentSort}
                    width={120}
                    align="right"
                  />
                  <DocumentSortHeader
                    label="Status"
                    column="status"
                    sortState={documentSort}
                    onSort={handleDocumentSort}
                    width={50}
                    align="center"
                  />
                  <DocumentSortHeader
                    label="Version"
                    column="version"
                    sortState={documentSort}
                    onSort={handleDocumentSort}
                    width={50}
                    align="center"
                  />
                  <Typography
                    level="body-xs"
                    fontWeight="lg"
                    sx={{ width: 40, textAlign: "center" }}
                  >
                    Access
                  </Typography>
                  <Box sx={{ width: 40 }} /> {/* Preview spacer */}
                  <Box sx={{ width: 40 }} /> {/* New version spacer */}
                  <Box sx={{ width: 40 }} /> {/* Edit spacer */}
                  <Box sx={{ width: 40 }} /> {/* Delete spacer */}
                </Box>

                {/* Root-level action bar when folders exist */}
                {hasFolders && (
                  <Box
                    onDragOver={(e) => handleDragOver(e, "/")}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, "/")}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      minWidth: DOCUMENT_TABLE_MIN_WIDTH,
                      py: 0.5,
                      px: 1,
                      borderBottom: "1px solid",
                      borderColor:
                        dragOverFolder === "/" ? "primary.500" : "divider",
                      bgcolor:
                        dragOverFolder === "/"
                          ? "primary.softBg"
                          : "background.surface",
                      outline: dragOverFolder === "/" ? "2px solid" : "none",
                      outlineColor:
                        dragOverFolder === "/" ? "primary.500" : "transparent",
                      transition: "background-color 0.15s, outline 0.15s",
                    }}
                  >
                    <Typography
                      level="body-xs"
                      sx={{ color: "text.tertiary", flex: 1 }}
                    >
                      Root
                    </Typography>
                    <Stack direction="row" spacing={0.5}>
                      <Tooltip title="Download all as ZIP">
                        <IconButton
                          size="sm"
                          variant="soft"
                          color="primary"
                          onClick={handleDownloadAll}
                          disabled={documents.length === 0}
                        >
                          <FolderZipIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip
                        title={
                          persistentExceeded
                            ? "Storage quota exceeded. Upgrade your plan to upload more files."
                            : "Upload files to root"
                        }
                      >
                        <span>
                          <IconButton
                            size="sm"
                            variant="soft"
                            color="primary"
                            onClick={() => triggerFileUpload("/")}
                            disabled={uploading || persistentExceeded}
                          >
                            <CloudUploadIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip
                        title={
                          persistentExceeded
                            ? "Storage quota exceeded. Upgrade your plan to upload more files."
                            : "Upload folder to root"
                        }
                      >
                        <span>
                          <IconButton
                            size="sm"
                            variant="soft"
                            color="primary"
                            onClick={() => triggerFolderUpload("/")}
                            disabled={uploading || persistentExceeded}
                          >
                            <DriveFolderUploadIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Create folder">
                        <IconButton
                          size="sm"
                          variant="soft"
                          color="neutral"
                          onClick={() => openCreateFolderModal("/")}
                          disabled={uploading}
                        >
                          <CreateNewFolderIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Box>
                )}

                {/* Tree content */}
                {renderTreeNode(folderTree)}
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Summary stats */}
        {documents.length > 0 && (
          <Stack direction="row" spacing={2}>
            <Card variant="soft" sx={{ flex: 1 }}>
              <CardContent>
                <Typography level="body-xs" textTransform="uppercase">
                  Total Documents
                </Typography>
                <Typography level="h3">{documents.length}</Typography>
              </CardContent>
            </Card>
            <Card variant="soft" sx={{ flex: 1 }}>
              <CardContent>
                <Typography level="body-xs" textTransform="uppercase">
                  Total Tokens
                </Typography>
                <Typography level="h3">
                  {documents
                    .reduce((sum, doc) => sum + doc.token_count, 0)
                    .toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
            <Card variant="soft" sx={{ flex: 1 }}>
              <CardContent>
                <Typography level="body-xs" textTransform="uppercase">
                  Indexed
                </Typography>
                <Typography level="h3">
                  {
                    documents.filter((d) => d.index_status === "completed")
                      .length
                  }{" "}
                  / {documents.length}
                </Typography>
              </CardContent>
            </Card>
          </Stack>
        )}
      </Stack>

      {/* Create folder modal */}
      <Modal
        open={createFolderModal}
        onClose={() => setCreateFolderModal(false)}
      >
        <ModalDialog>
          <ModalClose />
          <Typography level="h4">Create Folder</Typography>
          <Typography level="body-sm" sx={{ mb: 2, color: "text.secondary" }}>
            {createFolderParent === "/"
              ? "Create a new folder at root level"
              : `Create a subfolder in "${createFolderParent.split("/").pop()}"`}
          </Typography>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleCreateFolder();
            }}
          >
            <Stack spacing={2}>
              <FormControl>
                <FormLabel>Folder name</FormLabel>
                <Input
                  autoFocus
                  placeholder="Enter folder name"
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                />
              </FormControl>
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button
                  variant="plain"
                  color="neutral"
                  onClick={() => setCreateFolderModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="solid"
                  color="primary"
                  disabled={!newFolderName.trim()}
                  startDecorator={<AddIcon />}
                >
                  Create
                </Button>
              </Stack>
            </Stack>
          </form>
        </ModalDialog>
      </Modal>

      {/* Delete confirmation modal (single document) */}
      <Modal open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)}>
        <ModalDialog variant="outlined" role="alertdialog">
          <ModalClose />
          <Typography level="h4">Delete Document</Typography>
          <Typography level="body-md" sx={{ my: 2 }}>
            Are you sure you want to delete &quot;{deleteConfirm?.filename}
            &quot;? This will remove all indexed content and cannot be undone.
          </Typography>
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button
              variant="plain"
              color="neutral"
              onClick={() => setDeleteConfirm(null)}
            >
              Cancel
            </Button>
            <Button variant="solid" color="danger" onClick={handleDelete}>
              Delete
            </Button>
          </Stack>
        </ModalDialog>
      </Modal>

      {/* Folder delete confirmation modal */}
      <Modal
        open={!!folderDeleteConfirm}
        onClose={() => setFolderDeleteConfirm(null)}
      >
        <ModalDialog variant="outlined" role="alertdialog">
          <ModalClose />
          <Typography level="h4">Delete Folder</Typography>
          <Typography level="body-md" sx={{ my: 2 }}>
            {folderDeleteConfirm &&
            getDocIdsInFolder(folderDeleteConfirm).length > 0
              ? `Are you sure you want to delete the folder "${folderDeleteConfirm.name}" and all ${getDocIdsInFolder(folderDeleteConfirm).length} files inside? This will remove all indexed content and cannot be undone.`
              : `Are you sure you want to delete the empty folder "${folderDeleteConfirm?.name}"?`}
          </Typography>
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button
              variant="plain"
              color="neutral"
              onClick={() => setFolderDeleteConfirm(null)}
            >
              Cancel
            </Button>
            <Button variant="solid" color="danger" onClick={handleFolderDelete}>
              Delete Folder
            </Button>
          </Stack>
        </ModalDialog>
      </Modal>

      {/* Batch delete confirmation modal */}
      <Modal
        open={batchDeleteConfirm}
        onClose={() => setBatchDeleteConfirm(false)}
      >
        <ModalDialog variant="outlined" role="alertdialog">
          <ModalClose />
          <Typography level="h4">
            Delete {selectedIds.size} Documents
          </Typography>
          <Typography level="body-md" sx={{ my: 2 }}>
            Are you sure you want to delete {selectedIds.size} selected
            document(s)? This will remove all indexed content and cannot be
            undone.
          </Typography>
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button
              variant="plain"
              color="neutral"
              onClick={() => setBatchDeleteConfirm(false)}
            >
              Cancel
            </Button>
            <Button variant="solid" color="danger" onClick={handleBatchDelete}>
              Delete All
            </Button>
          </Stack>
        </ModalDialog>
      </Modal>

      {/* Version history modal */}
      <Modal open={!!versionModalDoc} onClose={handleCloseVersionModal}>
        <ModalDialog sx={{ minWidth: 400 }}>
          <ModalClose />
          <Typography level="h4" startDecorator={<HistoryIcon />}>
            Version History
          </Typography>
          <Typography level="body-sm" sx={{ mb: 2, color: "text.secondary" }}>
            {versionModalDoc?.filename}
          </Typography>

          {versionLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : versionHistory ? (
            <Stack spacing={2}>
              {/* Version selector */}
              <FormControl>
                <FormLabel>Select Version</FormLabel>
                <Select
                  value={selectedVersionId || ""}
                  onChange={(_, value) => {
                    setSelectedVersionId(value as string);
                    setShowDiff(false);
                  }}
                  placeholder="Select a version"
                >
                  {versionHistory.versions
                    .slice()
                    .reverse() // Show newest first
                    .map((v) => (
                      <Option key={v.id} value={v.id}>
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          sx={{ width: "100%" }}
                        >
                          <Typography
                            level="body-sm"
                            fontWeight={v.is_current ? "lg" : "md"}
                          >
                            Version {v.version}
                          </Typography>
                          {v.is_current && (
                            <Chip size="sm" variant="soft" color="success">
                              Current
                            </Chip>
                          )}
                          {v.is_generated && (
                            <Chip size="sm" variant="soft" color="primary">
                              Generated
                            </Chip>
                          )}
                          <Typography
                            level="body-xs"
                            sx={{ color: "text.tertiary", ml: "auto" }}
                          >
                            {v.uploaded_at ? formatDate(v.uploaded_at) : ""}
                          </Typography>
                        </Stack>
                      </Option>
                    ))}
                </Select>
              </FormControl>

              {/* Selected version info */}
              {selectedVersion && (
                <Card variant="soft" size="sm">
                  <CardContent>
                    {showDiff && currentVersion ? (
                      <VersionDiffView
                        workspaceId={workspace?.id || ""}
                        currentVersionId={currentVersion.id}
                        selectedVersionId={selectedVersion.id}
                        currentFilename={currentVersion.filename}
                        selectedFilename={selectedVersion.filename}
                        currentLabel={`Version ${currentVersion.version} (Current)`}
                        selectedLabel={`Version ${selectedVersion.version}`}
                        sharePointDocId={
                          selectedVersion.sp_version_id
                            ? versionModalDoc?.id
                            : undefined
                        }
                      />
                    ) : (
                      <Stack spacing={0.5}>
                        <Typography
                          level="body-xs"
                          sx={{ color: "text.secondary" }}
                        >
                          Version {selectedVersion.version}{" "}
                          {selectedVersion.is_current ? "(Current)" : ""}
                        </Typography>
                        {selectedVersion.generation_metadata
                          ?.prompt_context && (
                          <Typography
                            level="body-xs"
                            sx={{ color: "text.tertiary" }}
                          >
                            {selectedVersion.generation_metadata.prompt_context}
                          </Typography>
                        )}
                      </Stack>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Actions */}
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button
                  variant="plain"
                  color="neutral"
                  onClick={handleCloseVersionModal}
                >
                  Close
                </Button>
                <Button
                  variant="soft"
                  color="neutral"
                  startDecorator={<VisibilityIcon />}
                  onClick={handlePreviewVersion}
                  disabled={
                    !selectedVersion ||
                    !isPreviewableFormat(
                      selectedVersion.filename ||
                        versionModalDoc?.filename ||
                        "",
                    )
                  }
                >
                  Preview
                </Button>
                <Button
                  variant="soft"
                  color="primary"
                  startDecorator={
                    downloadingId === selectedVersionId ? (
                      <CircularProgress size="sm" />
                    ) : (
                      <DownloadIcon />
                    )
                  }
                  onClick={handleDownloadVersion}
                  disabled={
                    !selectedVersionId || downloadingId === selectedVersionId
                  }
                >
                  Download
                </Button>
                <Button
                  variant={showDiff ? "solid" : "soft"}
                  color="neutral"
                  startDecorator={<CompareArrowsIcon />}
                  onClick={() => setShowDiff((value) => !value)}
                  disabled={!selectedVersion || selectedVersion.is_current}
                >
                  {showDiff ? "Show details" : "Show diff"}
                </Button>
                {/* Show Restore button only for non-current versions */}
                {selectedVersion && !selectedVersion.is_current && (
                  <Button
                    variant="solid"
                    color="warning"
                    startDecorator={
                      restoring ? (
                        <CircularProgress size="sm" />
                      ) : (
                        <RestoreIcon />
                      )
                    }
                    onClick={handleRestoreVersion}
                    disabled={restoring}
                  >
                    Restore This Version
                  </Button>
                )}
              </Stack>
            </Stack>
          ) : (
            <Typography level="body-sm" sx={{ color: "text.secondary" }}>
              No version history available.
            </Typography>
          )}
        </ModalDialog>
      </Modal>

      {/* Document editor modal */}
      <DocumentEditorModal
        open={editorOpen}
        document={editorDocument}
        initialContent={editorContent}
        originalFormat={editorOriginalFormat}
        onClose={handleCloseEditor}
        onSave={handleSaveDocument}
        saving={editorSaving}
      />

      {/* Document preview modal */}
      <DocumentPreviewModal
        open={previewOpen}
        document={previewDocument}
        workspaceId={workspace?.id || ""}
        onClose={handleClosePreview}
        spVersionId={previewSpVersionId}
      />
    </Page>
  );
};

export default Documents;
